import { supabase } from "@/lib/supabaseClient";

const TRIONFI_TABLE = "Trionfi";
const TRIONFI_DISPLAY_VIEW = "trionfi_display_results";
const ASSET_PREFIX = "/trionfi-assets";

const PUBLIC_CARD_FIELDS = `
  card_id,
  selected_words,
  word_labels,
  locale,
  personal_reading,
  historical_description,
  archetype_title,
  archetype_system,
  created_at,
  public_slug,
  accession_number
`;

const PUBLIC_OVERVIEW_FIELDS = `
  card_id,
  archetype_title,
  archetype_system,
  created_at
`;

function cleanCardId(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "");
}

function cleanSlug(value) {
  return String(value ?? "").trim();
}

function clampLimit(value) {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return 120;
  return Math.min(200, Math.max(1, parsed));
}

function firstRow(data) {
  if (Array.isArray(data)) return data[0] ?? null;
  if (Array.isArray(data?.cards)) return data.cards[0] ?? null;
  return data?.card ?? data ?? null;
}

function rowsFrom(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.cards)) return data.cards;
  if (Array.isArray(data?.results)) return data.results;
  return data ? [data] : [];
}

function missingRpc(error) {
  const code = String(error?.code ?? "");
  const message = String(error?.message ?? "");

  return (
    code === "42883" ||
    code === "PGRST202" ||
    /function .* does not exist/i.test(message) ||
    /could not find the function/i.test(message)
  );
}

function normaliseCard(row) {
  if (!row || typeof row !== "object") return null;

  const cardId = cleanCardId(row.card_id);

  return {
    card_id: cardId,
    selected_words: Array.isArray(row.selected_words) ? row.selected_words : [],
    word_labels: Array.isArray(row.word_labels) ? row.word_labels : [],
    locale: row.locale === "nl" ? "nl" : "en",
    personal_reading: row.personal_reading ?? "",
    historical_description: row.historical_description ?? "",
    archetype_title: row.archetype_title ?? "",
    archetype_system: row.archetype_system ?? "",
    created_at: row.created_at ?? null,
    public_slug: row.public_slug ?? "",
    accession_number: row.accession_number ?? "",
    image_url: resolveTrionfiCardImage(cardId),
  };
}

function normaliseOverviewCard(row) {
  if (!row || typeof row !== "object") return null;

  const cardId = cleanCardId(row.card_id);
  if (!cardId) return null;

  return {
    card_id: cardId,
    archetype_title: row.archetype_title ?? "",
    archetype_system: row.archetype_system ?? "",
    created_at: row.created_at ?? null,
    image_url: resolveTrionfiCardImage(cardId),
  };
}

export function resolveTrionfiCardImage(cardId) {
  const cleanId = cleanCardId(cardId);

  return cleanId
    ? `${ASSET_PREFIX}/cards/archetypes/${cleanId}.webp`
    : `${ASSET_PREFIX}/cards/trionfi_back.png`;
}

export function formatTrionfiDate(value, locale = "en") {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(locale === "nl" ? "nl-NL" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

/**
 * Public detail lookup. The RPC is the intended boundary: it can expose one
 * card by an opaque public slug without granting anonymous table-wide reads.
 * The direct table query is a temporary compatibility path for deployments
 * made before the RPC migration reaches production.
 */
export async function getPublicTrionfiCard(publicSlug) {
  const slug = cleanSlug(publicSlug);
  if (!slug) throw new Error("Missing Trionfi card slug.");

  const { data: rpcData, error: rpcError } = await supabase.rpc(
    "get_public_trionfi_card",
    { p_public_slug: slug },
  );

  if (!rpcError) {
    const card = normaliseCard(firstRow(rpcData));
    if (!card) throw new Error("This Trionfi card could not be found.");
    return card;
  }

  if (!missingRpc(rpcError)) throw rpcError;

  const { data, error } = await supabase
    .from(TRIONFI_TABLE)
    .select(PUBLIC_CARD_FIELDS)
    .eq("public_slug", slug)
    .maybeSingle();

  if (error) throw error;

  const card = normaliseCard(data);
  if (!card) throw new Error("This Trionfi card could not be found.");
  return card;
}

/**
 * Public archive list. This intentionally excludes names, selected words and
 * readings. The transition fallback uses the existing public display view,
 * never the claim table.
 */
export async function listPublicTrionfiCards(limit = 120) {
  const safeLimit = clampLimit(limit);

  const { data: rpcData, error: rpcError } = await supabase.rpc(
    "list_public_trionfi_cards",
    { p_limit: safeLimit },
  );

  if (!rpcError) {
    return rowsFrom(rpcData).map(normaliseOverviewCard).filter(Boolean);
  }

  if (!missingRpc(rpcError)) throw rpcError;

  const { data, error } = await supabase
    .from(TRIONFI_DISPLAY_VIEW)
    .select(PUBLIC_OVERVIEW_FIELDS)
    .order("created_at", { ascending: false })
    .limit(safeLimit);

  if (error) throw error;
  return (data ?? []).map(normaliseOverviewCard).filter(Boolean);
}
